"use client";

import {
  ANNOUNCEMENT_CTA_LABEL,
  ANNOUNCEMENT_DISMISS_STORAGE_KEY,
  ANNOUNCEMENT_MESSAGE,
  ANNOUNCEMENT_PDF_URL,
} from "@/lib/constants/announcement";
import { X } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";

/**
 * Site-wide announcement bar.
 *
 * Renders a dismissible banner above the sticky header. The CTA links directly
 * to the printable PDF in a new tab. Dismissal is persisted in localStorage so the
 * bar stays hidden on subsequent visits.
 *
 * Rendering notes:
 * - Starts hidden and only reveals after the dismissal check runs on the
 *   client, avoiding a flash of the bar for users who already dismissed it and
 *   preventing hydration mismatches (server never knows localStorage state).
 */
export function AnnouncementBar() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(ANNOUNCEMENT_DISMISS_STORAGE_KEY) === "true";
      setIsVisible(!dismissed);
    } catch {
      // localStorage can throw in private mode / when blocked. Show the bar as
      // a sensible default rather than failing silently.
      setIsVisible(true);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    try {
      window.localStorage.setItem(ANNOUNCEMENT_DISMISS_STORAGE_KEY, "true");
    } catch {
      // Non-critical: dismissal simply won't persist across reloads.
    }
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <aside aria-label="Site announcement" className="pm-announcement-gradient relative text-black">
      <div className="container mx-auto flex items-center justify-center gap-3 px-10 py-2 text-center text-sm">
        <p className="m-0">
          <span className="font-medium">{ANNOUNCEMENT_MESSAGE}</span>
          <a
            href={ANNOUNCEMENT_PDF_URL}
            target="_blank"
            rel="noreferrer"
            className="ml-3 underline underline-offset-2 font-semibold hover:text-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-cta-yellow-bright rounded"
          >
            {ANNOUNCEMENT_CTA_LABEL}
          </a>
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss announcement"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-black/70 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-cta-yellow-bright rounded"
      >
        <X weight="bold" className="h-4 w-4" aria-hidden="true" />
      </button>
    </aside>
  );
}
